import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { markUserConfigExplainerVideoSeen, type UserConfig } from '@/entities/user-config';

import { userConfigApi } from './userConfig.api';
import { userConfigOptions, userConfigQueryKey } from './userConfig.options';

export const useUserConfig = () => useQuery(userConfigOptions());

export const useSaveUserConfig = (userEmail: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userConfig: UserConfig) => {
      const previous = queryClient.getQueryData<UserConfig>(userConfigQueryKey);
      return userConfigApi.save(userConfig, {
        userEmail,
        previousHomepage: previous?.homepage.value ?? ''
      });
    },
    onSuccess: saved => {
      queryClient.setQueryData(userConfigQueryKey, saved);
    }
  });
};

export const useMarkExplainerVideoSeen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: userConfigQueryKey });
      const previous = queryClient.getQueryData<UserConfig>(userConfigQueryKey);

      if (previous) {
        queryClient.setQueryData(userConfigQueryKey, markUserConfigExplainerVideoSeen(previous));
      }

      return { previous };
    },
    mutationFn: async () => {
      const userConfig = queryClient.getQueryData<UserConfig>(userConfigQueryKey);

      if (!userConfig) throw new Error('No user config in cache');

      return userConfigApi.markExplainerVideoSeen(userConfig);
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(userConfigQueryKey, ctx.previous);
    }
  });
};
